import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AddCompanyStackingItemsDto,
  AddStackingListResponse,
  StackingListData,
  StackingListDifferences,
  StackingListResponse,
  UpdateCompanyStackingItemsDto,
} from '@et/typings';
import { getDocPrepServiceDomain } from '@et/utils';
import { catchError, map, Observable, of, retry } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StackingService {
  private readonly baseUrl = getDocPrepServiceDomain();

  private http = inject(HttpClient);

  /**
   * Retrieves the stacking list for a specific company.
   *
   * This method sends a GET request to fetch the stacking list for the provided company ID.
   * If the request returns a 404 error, it falls back to fetching the default stacking list.
   * The response is retried once on failure, and the result is mapped to the data property.
   *
   * @param {string} companyId - The ID of the company to retrieve the stacking list for.
   * @returns {Observable<StackingListData[]>} An observable emitting the stacking list data.
   */
  getCompanyList(companyId: string): Observable<StackingListData[]> {
    const params = new HttpParams().append('companyId', companyId);
    return this.http
      .get<StackingListResponse>(
        `${this.baseUrl}/api/stacking/GetCompanyStackingList`,
        {
          params,
        },
      )
      .pipe(
        retry({ count: 3, delay: 300 }),
        map((res) => res.data),
        catchError((err) => {
          if (err.status === 404) {
            return of([]);
          }
          throw err;
        }),
      );
  }

  /**
   * Adds new stacking items to a company.
   *
   * This method sends a POST request to add the provided stacking items to the company.
   *
   * @param {string} companyId - The ID of the company to add stacking items to.
   * @param {AddCompanyStackingItemsDto} stackingItems - The stacking items to add.
   * @returns {Observable<StackingListData[]>} An observable emitting the added stacking items.
   */
  addCompanyStackingItem(
    companyId: string,
    stackingItems: AddCompanyStackingItemsDto,
  ): Observable<StackingListData[]> {
    const params = new HttpParams().append('companyId', companyId);
    return this.http
      .post<AddStackingListResponse>(
        `${this.baseUrl}/api/stacking/AddCompanyStackingList`,
        stackingItems,
        { params },
      )
      .pipe(
        retry({ count: 3, delay: 300 }),
        map((res) => JSON.parse(res.data)),
      );
  }

  /**
   * Updates one or more stacking items for a specific company.
   *
   * This method sends a PATCH request to update the provided stacking items for the company.
   *
   * @param {string} companyId - The ID of the company whose stacking items are to be updated.
   * @param {UpdateCompanyStackingItemsDto} updateDto - The DTO containing the stacking items to update.
   * @returns {Observable<StackingListResponse>} An observable containing the server's response.
   */
  updateCompanyStackingItem(
    companyId: string,
    updateDto: UpdateCompanyStackingItemsDto,
  ): Observable<StackingListResponse> {
    const params = new HttpParams().append('companyId', companyId);
    return this.http
      .patch<StackingListResponse>(
        `${this.baseUrl}/api/stacking/UpdateCompanyStackingOption`,
        updateDto,
        { params },
      )
      .pipe(retry({ count: 3, delay: 300 }));
  }

  /**
   * Deletes one or more stacking items for a specific company.
   *
   * This method sends a DELETE request to remove the provided stacking item IDs from the company.
   *
   * @param {string} companyId - The ID of the company whose stacking items are to be deleted.
   * @param {string[]} stackingIds - The array of stacking item IDs to delete.
   * @returns {Observable<StackingListResponse>} An observable representing the server's response.
   */
  deleteCompanyStackingItems(
    companyId: string,
    stackingIds: string[],
  ): Observable<StackingListResponse> {
    const params = new HttpParams().append('companyId', companyId);
    return this.http
      .delete<StackingListResponse>(
        `${this.baseUrl}/api/stacking/DeleteCompanyStackingOption`,
        {
          params,
          body: stackingIds,
        },
      )
      .pipe(retry({ count: 3, delay: 300 }));
  }

  /**
   * Retrieves the differences between the company's stacking list and Azure.
   *
   * This method sends a GET request to fetch the stacking list differences for the provided company ID.
   * The response includes errors found, duplicates in the stacking list, items in the stacking list not in Azure,
   * and items in Azure not in the stacking list. Retries the request up to 3 times on failure.
   *
   * @param {string} companyId - The ID of the company to retrieve stacking list differences for.
   * @returns {Observable<StackingListDifferences>} An observable emitting the stacking list differences.
   */
  getStackingListDifference(
    companyId: string,
  ): Observable<StackingListDifferences> {
    const params = new HttpParams().append('companyId', companyId);
    return this.http
      .get<StackingListDifferences>(
        `${this.baseUrl}/api/stacking/GetCompanyStackingListDifferences`,
        {
          params,
        },
      )
      .pipe(
        retry({ count: 3, delay: 300 }),
        map((res) => ({
          errorsFound: res.errorsFound,
          duplicatesInStackingList: res.duplicatesInStackingList || [],
          inStackingListNotAzure: res.inStackingListNotAzure || [],
          inAzureNotStackingList: res.inAzureNotStackingList || [],
        })),
      );
  }
}
